import React, { useEffect, useState } from 'react';
import { getApiBase } from '../lib/apiBase';

export default function PaymentRecordsPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState('');
  const [session, setSession] = useState('');
  const [smtp, setSmtp] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const request = async (path, options = {}) => {
    const res = await fetch(`${getApiBase()}${path}`, { ...options, headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(res.status === 401 ? 'Session expired. Please log in again.' : 'Unable to load payment records.');
    return res;
  };
  const load = async () => {
    try {
      const data = await (await request(`/api/admin/payment-records?limit=100&skip=${page * 100}`)).json();
      setRows(data.items); setTotal(data.total); setSmtp(data.smtp_configured); setMessage('');
    } catch (err) { setMessage(err.message); }
  };
  useEffect(() => { load(); }, [token, page]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    try {
      const blob = await (await request('/api/admin/payment-records/export')).blob();
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = 'sag-paid-payments.csv'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setMessage(err.message); }
  };
  const reconcile = async () => {
    if (!session.trim().startsWith('cs_')) { setMessage('Enter the Stripe Checkout session reference (cs_…).'); return; }
    setBusy(true);
    try { await request(`/api/admin/payment-records/reconcile/${encodeURIComponent(session.trim())}`, { method: 'POST' }); await load(); setSession(''); }
    catch (err) { setMessage(err.message); }
    finally { setBusy(false); }
  };
  return <section className="bg-white border border-slate-200 p-6 mt-6" data-testid="payment-records-panel">
    <h2 className="text-xl font-medium mb-2">SAG payment records</h2>
    <p className="text-sm text-slate-500 mb-4">Confirmed Stripe payments: {total}. Export for your accountant or QuickBooks reconciliation. CSV export does not automatically post entries to QuickBooks.</p>
    {!smtp && <p className="text-amber-700 mb-4">SAG email delivery is not configured. Receipt emails remain queued until the email service is configured. Stripe’s own receipt settings are separate.</p>}
    <div className="flex flex-wrap gap-3 mb-4">
      <button type="button" onClick={load} className="border p-2">Refresh</button>
      <button type="button" onClick={exportCsv} className="border p-2">Export paid payments CSV</button>
    </div>
    <div className="flex flex-wrap gap-2 mb-4">
      <input aria-label="Existing Stripe session reference" value={session} onChange={e => setSession(e.target.value)} placeholder="Existing payment reference: cs_…" className="border p-2 flex-1" />
      <button type="button" disabled={busy} onClick={reconcile} className="border p-2">{busy ? 'Checking…' : 'Recover existing receipt'}</button>
    </div>
    {message && <p role="alert" className="text-red-700 mb-3">{message}</p>}
    <div className="overflow-x-auto"><table className="w-full text-sm text-left"><thead><tr>{['Paid date','Receipt / invoice','Customer','Amount','Transaction','Email'].map(label => <th key={label} className="p-2 border-b">{label}</th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.order_id}>
        <td className="p-2">{row.paid_at ? new Date(row.paid_at).toLocaleString() : '—'}</td>
        <td className="p-2">{row.receipt_number}<br />{row.invoice_number}</td>
        <td className="p-2">{row.customer_name}<br />{row.customer_email}</td>
        <td className="p-2">{row.currency} {row.amount}</td>
        <td className="p-2 break-all">{row.transaction_id}<br /><small>{row.order_id}</small></td>
        <td className="p-2">{row.email_status}</td>
      </tr>)}</tbody></table></div>
    {!rows.length && <p className="text-slate-500 mt-3">No confirmed paid records on this page. Recover an existing payment using its Stripe session reference.</p>}
    <div className="flex gap-3 mt-4"><button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button><span>Page {page + 1}</span><button type="button" disabled={(page + 1) * 100 >= total} onClick={() => setPage(page + 1)}>Next</button></div>
  </section>;
}
